import { Icons, Option, Select, Tooltip } from "@ster/ster-toolkit";
import { Select as SelectAntD } from "antd";
import { SelectValue } from "antd/lib/select";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useRecoilValue } from "recoil";

import { appSettingsState, packagesState } from "../../atoms";
import { Medium, PackageData } from "../../types";
import { getChannelName, getTargetGroupName } from "../../utils";
import getChannelImage from "../channelImages";
import styles from "./ChannelSelect.module.less";

const ChannelSelect = ({
  medium,
  period,
  value,
  onChange,
}: {
  medium: Medium;
  period: [Date, Date];
  value?: PackageData;
  onChange?: (v: PackageData) => void;
}) => {
  const { defaultRadioChannel } = useRecoilValue(appSettingsState);
  const { packages, simpleGroup: packagesSimple } = useRecoilValue(
    packagesState({
      medium,
      monthNumber: period[0].getMonth() + 1,
      year: period[0].getFullYear(),
    }),
  );
  const selectedPackages = packagesSimple[0].packages;

  const multiChannels = (
    selectedPackages?.filter((s) => s.channels?.length > 1) ?? []
  ).sort((a, b) =>
    // eslint-disable-next-line no-nested-ternary
    a.code === "ALGEMEEN NUT"
      ? 1
      : b.code === "ALGEMEEN NUT"
        ? -1
        : a.targetGroupId.localeCompare(b.targetGroupId),
  );

  const singleChannels = useMemo(
    () => selectedPackages?.filter((s) => s.channels?.length === 1) ?? [],
    [selectedPackages],
  );
  const handleChange = useCallback(
    (selectedId: SelectValue) => {
      const selectedSimplePackage = selectedPackages?.find(
        (t) => t.id.toString() === selectedId?.toString(),
      );
      const selectedPackage = packages
        .map(({ targetGroups }) =>
          targetGroups.find(
            ({ packageCode, targetGroupId, channels }) =>
              packageCode === selectedSimplePackage?.code &&
              targetGroupId === selectedSimplePackage?.targetGroupId &&
              channels.join(",") === selectedSimplePackage?.channels?.join(","),
          ),
        )
        .find((s) => s);

      onChange?.({
        packageCode: selectedPackage?.packageCode,
        targetGroupIndex: selectedPackage?.index,
        targetGroupId: selectedPackage?.targetGroupId,
        id: selectedId ? Number(selectedId?.toString()) : undefined,
      });
    },
    [onChange, packages, selectedPackages],
  );

  useEffect(() => {
    if (
      value?.packageCode &&
      !selectedPackages?.some(
        (p) =>
          p.code === value?.packageCode &&
          p.targetGroupId === value?.targetGroupId,
      )
    ) {
      handleChange(undefined);
    }
  }, [
    value?.packageCode,
    selectedPackages,
    handleChange,
    value?.targetGroupId,
  ]);

  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (!appliedDefaultRef.current && defaultRadioChannel) {
      const matchedChannel = singleChannels.find(
        ({ channels }) =>
          getChannelName(channels.join(", "))?.localeCompare(
            defaultRadioChannel,
            undefined,
            { sensitivity: "base" },
          ) === 0,
      );
      if (matchedChannel) {
        handleChange(matchedChannel.id);
        appliedDefaultRef.current = true;
      }
    }
  }, [defaultRadioChannel, singleChannels, selectedPackages, handleChange]);

  return (
    <Select
      value={value?.id}
      onChange={handleChange}
      placeholder="Kies een zender of doelgroep"
      id="channel"
      className="channelSelect"
      listHeight={400}
      popupClassName={styles.popup}
      optionLabelProp="children"
    >
      <SelectAntD.OptGroup label="Zender">
        {singleChannels?.map(({ channels, id }) => (
          <Option key={id} value={id}>
            <div className="channels">
              {channels.map((c) => getChannelImage(c))}
            </div>
            <div>{getChannelName(channels.join(", "))}</div>
          </Option>
        ))}
      </SelectAntD.OptGroup>
      {multiChannels.length > 0 && (
        <SelectAntD.OptGroup label="Combinatiepakket">
          {multiChannels?.map(({ id, targetGroupId, channels }) =>
            getTargetGroupName(targetGroupId) === "Algemeen nut" ? (
              <Option key={id} value={id}>
                {getTargetGroupName(targetGroupId)}
                <Tooltip
                  title={
                    <>
                      Enkel voor niet-commerciële organisaties met een
                      niet-commerciële boodschap. Voorwaarden zijn van
                      toepassing.
                    </>
                  }
                >
                  <span className="help-text">
                    <Icons.QuestionIcon width={16} height={16} />
                  </span>
                </Tooltip>
              </Option>
            ) : (
              <Option key={id} value={id}>
                {getTargetGroupName(targetGroupId)}
                <div className="channels multi">
                  {channels.map((c) => getChannelImage(c))}
                </div>
              </Option>
            ),
          )}
        </SelectAntD.OptGroup>
      )}
    </Select>
  );
};

export default memo(ChannelSelect);
